import { useState } from "react";
import { Frame, Node, Flow, Detail } from "@/components/diagrams/kit";
import { Switch } from "@/components/ui/switch";

const SOURCES = [
  { id: "graph", label: "Plant knowledge graph", sub: "Plant → Line → Machine → Tag", tone: "data", blind: "Identity blindness", effect: "The system no longer knows which line, machine or tag “the capper” means." },
  { id: "connectors", label: "Deterministic data connectors", sub: "SCADA · historian · MES · ERP", tone: "data", blind: "Time & quality blindness", effect: "No timestamps, units or quality flags — a stale torque value looks current." },
  { id: "docs", label: "Document retrieval", sub: "SOPs · manuals · shift logs", tone: "neutral", blind: "Semantic & workflow blindness", effect: "Answers lose the approved procedure and the shift context behind the numbers." },
];

export default function DataBlindArchitecture() {
  const [on, setOn] = useState({ graph: true, connectors: true, docs: true, security: true });
  const [sel, setSel] = useState("graph");
  const missing = SOURCES.filter((s) => !on[s.id]);
  const blind = [...missing.map((m) => m.blind), ...(!on.security ? ["Permission blindness"] : [])];
  const selected = SOURCES.find((s) => s.id === sel);
  return (
    <Frame title="Data Blind Architecture" hint="Switch capabilities off" testId="diagram-data-blind">
      <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
        <div>
          <Node tone="neutral" testId="dba-question">Operator / plant manager question</Node>
          <Flow vertical />
          <Node tone="trust" testId="dba-app">Governed AI application layer</Node>
          <Flow vertical />
          <Node tone="accent" testId="dba-planner">Tool / query planner · allow-listed functions</Node>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {SOURCES.map((s) => (
              <Node key={s.id} testId={`dba-${s.id}`} tone={on[s.id] ? s.tone : "danger"} active={sel === s.id} dim={!on[s.id]} onClick={() => setSel(s.id)}>
                <div className="font-semibold">{s.label}</div><div className="text-muted-foreground">{s.sub}</div>
              </Node>
            ))}
          </div>
          <Flow vertical on={missing.length < 3} />
          <Node tone="action" testId="dba-context">Context pack · asset identity · time window · units · quality flags</Node>
          <Flow vertical />
          <Node tone="primary" testId="dba-model">LLM / SLM · reasoning and explanation</Node>
          <Flow vertical />
          <div data-testid="dba-answer" className={`border p-3 text-sm ${blind.length ? "border-destructive bg-destructive/10" : "border-trust bg-trust text-white"}`}>
            {blind.length ? <><strong>Fluent but blind answer.</strong> Missing: {blind.join(" · ")}</> : <><strong>Answer with evidence</strong> — source · timestamp · confidence · next action</>}
          </div>
        </div>
        <div className="space-y-3 border border-border bg-card p-4">
          <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Capabilities</div>
          {[...SOURCES.map((s) => [s.id, s.label]), ["security", "Security plane (RBAC · secrets · audit)"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between gap-2 text-xs">
              <span>{l}</span>
              <Switch data-testid={`dba-switch-${k}`} checked={on[k]} onCheckedChange={(c) => setOn({ ...on, [k]: c })} />
            </label>
          ))}
        </div>
      </div>
      <Detail testId="dba-detail"><span key={sel + on[sel]}><strong>{selected.label}:</strong> {on[sel] ? `Prevents ${selected.blind.toLowerCase()}.` : selected.effect}</span></Detail>
    </Frame>
  );
}
